import { useState } from 'react';
import { Target, TrendingUp, Video, Loader2, AlertCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

interface Goal {
  id: string;
  type: string;
  target: number;
  current: number;
  market: string;
  period: string;
  progress: number;
  status: string;
}

export default function Goals() {
  const queryClient = useQueryClient();
  const [showNewGoal, setShowNewGoal] = useState(false);
  const [goalType, setGoalType] = useState('gmv');
  const [market, setMarket] = useState('DE');
  const [targetValue, setTargetValue] = useState('');
  const [period, setPeriod] = useState('month');

  const { data: goals, isLoading, error } = useQuery({
    queryKey: ['goals'],
    queryFn: async () => {
      const response = await api.get<Goal[]>('/goals');
      return response.data;
    },
  });

  const createGoalMutation = useMutation({
    mutationFn: async (newGoal: { type: string; market: string; target: string; period: string }) => {
      const response = await api.post('/goals', newGoal);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      setShowNewGoal(false);
      setTargetValue('');
    },
  });

  const handleCreateGoal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetValue) return;
    createGoalMutation.mutate({
      type: goalType,
      market,
      target: targetValue,
      period,
    });
  };

  const formatValue = (type: string, value: number) => {
    if (type === 'gmv') return `€${value.toLocaleString()}`;
    return `${value} ${type === 'content_generation' ? 'videos' : 'units'}`;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
        <p className="text-gray-400">Loading goals...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4 text-red-500">
        <AlertCircle className="h-8 w-8" />
        <p>Failed to load goals. Please check if the API is running.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Goals</h1>
          <p className="text-gray-400 mt-1">Set and track business objectives</p>
        </div>
        <button 
          onClick={() => setShowNewGoal(!showNewGoal)}
          className="btn-primary flex items-center"
        >
          <Target className="h-5 w-5 mr-2" />
          New Goal
        </button>
      </div>

      {showNewGoal && (
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Create New Goal</h2>
          <form onSubmit={handleCreateGoal}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Goal Type</label>
                <select 
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary-500"
                  value={goalType}
                  onChange={(e) => setGoalType(e.target.value)}
                >
                  <option value="gmv">GMV Target</option>
                  <option value="content_generation">Content Generation</option>
                  <option value="product_launch">Product Launch</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Market</label>
                <select 
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary-500"
                  value={market}
                  onChange={(e) => setMarket(e.target.value)}
                >
                  <option value="DE">Germany (DE)</option>
                  <option value="AE">UAE (AE)</option>
                  <option value="SA">Saudi Arabia (SA)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Target Value</label>
                <input 
                  type="text" 
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary-500" 
                  placeholder={goalType === 'gmv' ? "e.g., 50000" : "e.g., 60"} 
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Period</label>
                <select 
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary-500"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                  <option value="quarter">Quarterly</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                type="button" 
                onClick={() => setShowNewGoal(false)} 
                className="px-4 py-2 text-gray-400 hover:text-white font-medium text-sm transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={createGoalMutation.isPending || !targetValue}
                className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createGoalMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Goal'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        {Array.isArray(goals) && goals.length > 0 ? (
          goals.map((goal) => (
            <div key={goal.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-4">
                  {goal.type === 'gmv' ? (
                    <TrendingUp className="h-10 w-10 text-primary-500" />
                  ) : (
                    <Video className="h-10 w-10 text-primary-500" />
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-white capitalize">
                      {goal.type.replace('_', ' ')}
                    </h3>
                    <p className="text-gray-400 text-sm">{goal.market} • {goal.period}</p>
                  </div>
                </div>
                <span className={`
                  px-3 py-1 rounded-full text-sm font-medium
                  ${goal.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}
                `}>
                  {goal.status.replace('_', ' ')}
                </span>
              </div>
              
              <div className="mt-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Progress</span>
                  <span className="text-white font-medium">{formatValue(goal.type, goal.current)} / {formatValue(goal.type, goal.target)}</span>
                </div>
                <div className="h-3 bg-dark-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary-600 to-primary-400 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(goal.progress, 100)}%` }}
                  />
                </div>
                <p className="text-right text-sm text-gray-400 mt-1">{goal.progress}% complete</p>
              </div>
            </div>
          ))
        ) : (
          <div className="py-12 text-center text-gray-400">
            No goals set yet. Create your first business goal above.
          </div>
        )}
      </div>
    </div>
  );
}
